import React, { useState, useEffect } from 'react';

interface BotUser {
  id: string;
  botUserId: string;
  botEmail: string;
  botName: string;
  botGender: string;
  enabled: boolean;
  todayLikeCount: number;
  totalLikes: number;
}

const LikeBotPage: React.FC = () => {
  const [settings, setSettings] = useState({
    dailyLimitPerUser: 100,
    intervalMinutes: 3
  });
  const [botUsers, setBotUsers] = useState<BotUser[]>([]);
  const [stats, setStats] = useState({
    todayLikes: 0,
    totalLikes: 0,
    totalMatches: 0,
    activeBotCount: 0
  });
  const [history, setHistory] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addingUser, setAddingUser] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const FUNCTIONS_URL = 'https://us-central1-datezy-production.cloudfunctions.net';

  // Ayarları yükle
  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${FUNCTIONS_URL}/getLikeBotSettingsApi`);
      const data = await response.json();

      if (data.success) {
        setSettings({
          dailyLimitPerUser: data.settings?.dailyLimitPerUser || 100,
          intervalMinutes: data.settings?.intervalMinutes || 3
        });
        setBotUsers(data.botUsers || []);
        setHistory(data.history || []);
      } else {
        setError(data.error || 'Ayarlar yüklenemedi');
      }
    } catch (err) {
      console.error('Ayar yükleme hatası:', err);
      setError('Bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  // İstatistikleri yükle
  const loadStats = async () => {
    try {
      const response = await fetch(`${FUNCTIONS_URL}/getLikeBotStats`);
      const data = await response.json();

      if (data.success) {
        setStats({
          todayLikes: data.stats.todayLikes || 0,
          totalLikes: data.stats.totalLikes || 0,
          totalMatches: data.stats.totalMatches || 0,
          activeBotCount: data.stats.activeBotCount || 0
        });
      }
    } catch (err) {
      console.error('İstatistik yükleme hatası:', err);
    }
  };

  // Genel ayarları kaydet
  const saveSettings = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${FUNCTIONS_URL}/updateLikeBotSettings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Ayarlar kaydedildi!');
      } else {
        setError(data.error || 'Kaydetme hatası');
        alert('❌ Hata: ' + (data.error || 'Kaydetme hatası'));
      }
    } catch (err) {
      console.error('Kaydetme hatası:', err);
      setError('Bağlantı hatası');
      alert('❌ Bağlantı hatası!');
    } finally {
      setSaving(false);
    }
  };

  // Yeni bot kullanıcısı ekle
  const addBotUser = async () => {
    if (!newEmail.trim()) {
      alert('Lütfen email girin');
      return;
    }

    try {
      setAddingUser(true);
      setError(null);

      const response = await fetch(`${FUNCTIONS_URL}/addLikeBotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ botEmail: newEmail.trim() })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ ' + data.message);
        setNewEmail('');
        loadSettings();
        loadStats();
      } else {
        alert('❌ Hata: ' + (data.error || 'Ekleme hatası'));
      }
    } catch (err) {
      console.error('Ekleme hatası:', err);
      alert('❌ Bağlantı hatası!');
    } finally {
      setAddingUser(false);
    }
  };

  // Bot kullanıcısı sil
  const removeBotUser = async (docId: string, botName: string) => {
    if (!window.confirm(`${botName} kullanıcısını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      const response = await fetch(`${FUNCTIONS_URL}/removeLikeBotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Kullanıcı silindi');
        loadSettings();
        loadStats();
      } else {
        alert('❌ Hata: ' + (data.error || 'Silme hatası'));
      }
    } catch (err) {
      console.error('Silme hatası:', err);
      alert('❌ Bağlantı hatası!');
    }
  };

  // Bot kullanıcısı aç/kapat
  const toggleBotUser = async (docId: string, currentEnabled: boolean) => {
    try {
      const response = await fetch(`${FUNCTIONS_URL}/toggleLikeBotUser`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ docId, enabled: !currentEnabled })
      });

      const data = await response.json();

      if (data.success) {
        loadSettings();
        loadStats();
      } else {
        alert('❌ Hata: ' + (data.error || 'Toggle hatası'));
      }
    } catch (err) {
      console.error('Toggle hatası:', err);
      alert('❌ Bağlantı hatası!');
    }
  };

  useEffect(() => {
    loadSettings();
    loadStats();
  }, []);

  // Tarih formatla
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Bilinmiyor';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
      return date.toLocaleString('tr-TR');
    } catch {
      return 'Bilinmiyor';
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #e74c3c',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ color: '#2c3e50', margin: '0 0 0.5rem 0', fontSize: '1.8rem' }}>
          💕 Beğeni Botu Yönetimi
        </h1>
        <p style={{ color: '#7f8c8d', margin: 0 }}>
          Otomatik beğeni botu ayarlarını yönetin
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          padding: '1rem',
          marginBottom: '1.5rem',
          color: '#721c24'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* İstatistikler */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>💕</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e74c3c' }}>
            {stats.todayLikes}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Bugünkü Beğeni</div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>❤️</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#9b59b6' }}>
            {stats.totalLikes}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Toplam Beğeni</div>
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#27ae60' }}>
            {stats.totalMatches}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Toplam Eşleşme</div>
        </div>

        <div style={{
          backgroundColor: stats.activeBotCount > 0 ? '#d4edda' : '#f8d7da',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>
            {stats.activeBotCount > 0 ? '🟢' : '🔴'}
          </div>
          <div style={{ 
            fontSize: '1.5rem', 
            fontWeight: 'bold', 
            color: stats.activeBotCount > 0 ? '#155724' : '#721c24' 
          }}>
            {stats.activeBotCount} / {botUsers.length}
          </div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>Aktif Bot</div>
        </div>
      </div>

      {/* Bot Kullanıcıları */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: '0 0 1.5rem 0', color: '#2c3e50' }}>👥 Bot Kullanıcıları</h2>

        {/* Yeni kullanıcı ekleme */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
          <input
            type="email"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="Kullanıcı email adresi"
            style={{
              flex: '1',
              minWidth: '250px',
              padding: '0.75rem 1rem',
              border: '2px solid #e0e0e0',
              borderRadius: '8px',
              fontSize: '1rem'
            }}
          />
          <button
            onClick={addBotUser}
            disabled={addingUser}
            style={{
              backgroundColor: addingUser ? '#95a5a6' : '#27ae60',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: addingUser ? 'not-allowed' : 'pointer'
            }}
          >
            {addingUser ? '⏳ Ekleniyor...' : '➕ Kullanıcı Ekle'}
          </button>
        </div>

        {/* Kullanıcı listesi */}
        {botUsers.length === 0 ? (
          <div style={{ 
            padding: '2rem', 
            textAlign: 'center', 
            color: '#666',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px'
          }}>
            Henüz bot kullanıcısı eklenmemiş
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {botUsers.map((user) => (
              <div
                key={user.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '1rem 1.5rem',
                  backgroundColor: user.enabled ? '#f8fff8' : '#fff8f8',
                  borderRadius: '8px',
                  border: `2px solid ${user.enabled ? '#27ae60' : '#e74c3c'}`,
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    backgroundColor: user.enabled ? '#27ae60' : '#e74c3c'
                  }} />
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '1.1rem' }}>
                      {user.botName}
                      <span style={{ 
                        marginLeft: '0.5rem', 
                        fontSize: '0.8rem', 
                        color: '#666',
                        fontWeight: 'normal'
                      }}>
                        ({user.botGender})
                      </span>
                    </div>
                    <div style={{ fontSize: '0.85rem', color: '#666' }}>
                      {user.botEmail}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#e74c3c' }}>
                      {user.todayLikeCount || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>Bugün</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontWeight: '600', color: '#9b59b6' }}>
                      {user.totalLikes || 0}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666' }}>Toplam</div>
                  </div>

                  <button
                    onClick={() => toggleBotUser(user.id, user.enabled)}
                    style={{
                      backgroundColor: user.enabled ? '#f39c12' : '#27ae60',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    {user.enabled ? '⏸️ Durdur' : '▶️ Başlat'}
                  </button>

                  <button
                    onClick={() => removeBotUser(user.id, user.botName)}
                    style={{
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ Sil
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Genel Ayarlar */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        padding: '2rem',
        marginBottom: '2rem'
      }}>
        <h2 style={{ margin: '0 0 1.5rem 0', color: '#2c3e50' }}>⚙️ Genel Ayarlar</h2>

        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {/* Kullanıcı başı günlük limit */}
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
              Kullanıcı Başı Günlük Beğeni Limiti
            </label>
            <input
              type="number"
              value={settings.dailyLimitPerUser}
              onChange={(e) => setSettings({ ...settings, dailyLimitPerUser: parseInt(e.target.value) || 100 })}
              min="1"
              max="500"
              style={{
                width: '200px',
                padding: '0.75rem 1rem',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
            <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '0.5rem' }}>
              Her bot kullanıcısı günde en fazla bu kadar beğeni yapar
            </p>
          </div>

          {/* Kaydet Butonu */}
          <div style={{ marginTop: '1rem' }}>
            <button
              onClick={saveSettings}
              disabled={saving}
              style={{
                backgroundColor: saving ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontSize: '1.1rem',
                fontWeight: '600',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {saving ? '⏳ Kaydediliyor...' : '💾 Ayarları Kaydet'}
            </button>
          </div>
        </div>
      </div>

      {/* Beğeni Geçmişi */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: '#2c3e50' }}>📜 Beğeni Geçmişi</h2>
          <button
            onClick={() => { loadSettings(); loadStats(); }}
            style={{
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Yenile
          </button>
        </div>

        {history.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            Henüz beğeni geçmişi yok
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem' }}>
                    Bot Kullanıcı
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem' }}>
                    Beğenilen Kullanıcı
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'center', color: '#666', fontSize: '0.85rem' }}>
                    Eşleşme
                  </th>
                  <th style={{ padding: '1rem', textAlign: 'left', color: '#666', fontSize: '0.85rem' }}>
                    Tarih
                  </th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={item.id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '1rem' }}>
                      <strong>{item.botEmail || 'Bilinmiyor'}</strong>
                    </td>
                    <td style={{ padding: '1rem' }}>
                      <strong>{item.targetUserName || 'İsimsiz'}</strong>
                      <div style={{ fontSize: '0.8rem', color: '#999' }}>{item.targetUserId}</div>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {item.matchCreated ? (
                        <span style={{
                          backgroundColor: '#d4edda',
                          color: '#155724',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem'
                        }}>
                          🎉 Eşleşti
                        </span>
                      ) : (
                        <span style={{
                          backgroundColor: '#f8f9fa',
                          color: '#666',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.8rem'
                        }}>
                          —
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', color: '#666', fontSize: '0.9rem' }}>
                      {formatDate(item.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default LikeBotPage;
